import { type TimecodeSpec, TimecodeError } from "./framerate.js";
import { framesToParts, partsToFrames, wrapFrames, type TimecodeParts } from "./frames.js";

const p2 = (n: number): string => String(n).padStart(2, "0");

export interface FormatOptions {
  /**
   * Bruk `;` foran frames når spesifikasjonen er drop-frame. Det er
   * konvensjonen i utveksling mot klipp. Grensesnittet bruker `:` overalt,
   * fordi 25 fps er normalen her og skillet bare forvirrer i felt.
   */
  readonly dropFrameSeparator?: boolean;
}

export function formatTimecode(
  frames: number,
  spec: TimecodeSpec,
  opts: FormatOptions = {},
): string {
  const p = framesToParts(frames, spec);
  const sep = opts.dropFrameSeparator === true && spec.dropFrame ? ";" : ":";
  return `${p2(p.hours)}:${p2(p.minutes)}:${p2(p.seconds)}${sep}${p2(p.frames)}`;
}

/** Fortegnssatt differanse, til «alle poster flyttes fra … til …». */
export function formatOffset(frames: number, spec: TimecodeSpec): string {
  const sign = frames < 0 ? "−" : "+";
  const abs = Math.abs(Math.trunc(frames));
  const n = spec.rate.nominal;
  const hh = Math.floor(abs / (n * 3600));
  const mm = Math.floor((abs % (n * 3600)) / (n * 60));
  const ss = Math.floor((abs % (n * 60)) / n);
  return `${sign}${p2(hh)}:${p2(mm)}:${p2(ss)}:${p2(abs % n)}`;
}

const PATTERN = /^(\d{1,2})[:;.](\d{1,2})[:;.](\d{1,2})[:;.](\d{1,3})$/;

/**
 * Leser brukerinput. Kaster på verdier som ikke finnes, inkludert
 * framenumre drop-frame hopper over.
 */
export function parseTimecode(input: string, spec: TimecodeSpec): number {
  const m = PATTERN.exec(input.trim());
  if (!m) throw new TimecodeError(`Ugyldig tidskode: «${input}»`);
  const parts: TimecodeParts = {
    hours: Number(m[1]),
    minutes: Number(m[2]),
    seconds: Number(m[3]),
    frames: Number(m[4]),
  };
  return partsToFrames(parts, spec);
}

/** Bumper ett av de fire tallene, slik hjulene i grensesnittet gjør. */
export type TimecodeUnit = "hours" | "minutes" | "seconds" | "frames";

export function unitInFrames(unit: TimecodeUnit, spec: TimecodeSpec): number {
  const n = spec.rate.nominal;
  switch (unit) {
    case "hours":
      return n * 3600;
    case "minutes":
      return n * 60;
    case "seconds":
      return n;
    case "frames":
      return 1;
  }
}

export function bumpUnit(
  frames: number,
  unit: TimecodeUnit,
  direction: 1 | -1,
  spec: TimecodeSpec,
): number {
  return wrapFrames(frames + direction * unitInFrames(unit, spec), spec);
}
